/**
 * @file Reusable hook for OAuth connection flow.
 * Encapsulates OAuth window handling, connection creation, post-connection config state,
 * and config dialog rendering helpers. Used by both Settings ConnectionsTab and Onboarding.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '@/store/appStore';
import { useCreateConnection, useCreateDataset } from '@/hooks/useConnections';
import { useCloseWindow } from '@/hooks/useCloseWindow';
import { openOAuthWindow } from '@/hooks/useOauth';
import { toast } from '@strivelabs/ui-core';
import { logger } from '@/lib/logger';
import type { App } from '@/services/DataService';
import type { OauthPayload, PostConnectionConfig } from '@/types/connections';

const APPS_REQUIRING_CONFIG = ['google_ads', 'google_analytics', 'google_search_console', 'google_ads_dataset'];

/**
 * Apps whose dataset is created automatically as soon as the connection is made
 * (right before the config modal opens), instead of via a manual sync toggle.
 */
const APPS_WITH_AUTO_DATASET = ['google_analytics'];

interface UseConnectionFlowOptions {
  apps: App[];
  onConnectionChange?: () => void;
}

export function useConnectionFlow({ apps, onConnectionChange }: UseConnectionFlowOptions) {
  const [connectingApp, setConnectingApp] = useState<App | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const connectingRef = useRef(false);
  const [postConnectionConfig, setPostConnectionConfig] = useState<PostConnectionConfig | null>(null);

  const createConnectionMutation = useCreateConnection();
  const createDatasetMutation = useCreateDataset();

  const accessToken = useAppStore((state) => state.authDetails.accessToken);
  const refreshToken = useAppStore((state) => state.authDetails.refreshToken);
  const userId = useAppStore((state) => state.authDetails.userId);
  const userName = useAppStore((state) => state.authDetails.userName);
  const userEmail = useAppStore((state) => state.authDetails.userEmail);
  const setAuthDetails = useAppStore((state) => state.setAuthDetails);

  useCloseWindow();

  const [basicAuthApp, setBasicAuthApp] = useState<App | null>(null);
  const [apiKeyApp, setApiKeyApp] = useState<App | null>(null);

  const handleConnectApp = useCallback(
    (app: App) => {
      if (isConnecting) return;
      const authType = app.auth?.[0]?.type;
      if (authType === 'basic') {
        // Basic-auth apps use a modal flow instead of an OAuth popup.
        setBasicAuthApp(app);
        return;
      }
      if (authType === 'api_key') {
        // API-key apps collect the key in a dedicated modal — no OAuth popup.
        setApiKeyApp(app);
        return;
      }
      setConnectingApp(app);
      setIsConnecting(true);
      connectingRef.current = false;
      openOAuthWindow(app.short_name, 'default');
    },
    [isConnecting],
  );

  const closeBasicAuthModal = useCallback(() => {
    setBasicAuthApp(null);
  }, []);

  const handleBasicAuthSuccess = useCallback(() => {
    setBasicAuthApp(null);
    onConnectionChange?.();
  }, [onConnectionChange]);

  const closeApiKeyModal = useCallback(() => {
    setApiKeyApp(null);
  }, []);

  const handleApiKeySuccess = useCallback(() => {
    setApiKeyApp(null);
    onConnectionChange?.();
  }, [onConnectionChange]);

  const connectAppByShortName = useCallback(
    (shortName: string) => {
      const app = apps.find((a) => a.short_name === shortName);
      if (app) handleConnectApp(app);
    },
    [apps, handleConnectApp],
  );

  // Handle OAuth completion for new connections
  useEffect(() => {
    if (
      isConnecting &&
      connectingApp &&
      accessToken &&
      !connectingRef.current &&
      !createConnectionMutation.isPending
    ) {
      connectingRef.current = true;

      const createConnection = async () => {
        try {
          const oauthPayload: OauthPayload = {
            app_name: connectingApp.short_name,
            display_name: connectingApp.display_name,
            auth_name: 'default',
            token_type: 'oauth',
            access_token: accessToken as string,
            refresh_token: refreshToken as string,
            principal_id: userId as string,
            principal_name: userName as string,
            principal_email: userEmail as string,
          };

          const result = await createConnectionMutation.mutateAsync({
            oauthPayload,
            appName: connectingApp.short_name,
          });

          setAuthDetails({
            userId: '',
            userName: '',
            userEmail: '',
            accessToken: '',
            refreshToken: '',
          });

          const credentialId = result?.credential?.id;

          if (APPS_REQUIRING_CONFIG.includes(connectingApp.short_name)) {
            if (!credentialId) {
              toast({
                title: 'Error',
                description: 'Failed to retrieve credential. Please try reconnecting.',
                variant: 'destructive',
              });
              onConnectionChange?.();
              return;
            }

            if (APPS_WITH_AUTO_DATASET.includes(connectingApp.short_name)) {
              try {
                await createDatasetMutation.mutateAsync({
                  credentialId,
                  displayName: connectingApp.display_name,
                });
              } catch (datasetErr) {
                logger(datasetErr as Error, { context: 'Error creating dataset on connection' });
              }
            }

            setPostConnectionConfig({
              credentialId,
              appName: connectingApp.short_name,
              displayName: connectingApp.display_name,
            });
            toast({
              title: 'Connected',
              description: `${connectingApp.display_name} connected. Please complete the configuration.`,
            });
          } else {
            toast({
              title: 'Success',
              description: `${connectingApp.display_name} connected successfully`,
            });
          }

          onConnectionChange?.();
        } catch (err) {
          logger(err as Error, { context: 'Error creating connection' });
          toast({
            title: 'Error',
            description: 'Failed to create connection. Please try again.',
            variant: 'destructive',
          });
        } finally {
          setIsConnecting(false);
          setConnectingApp(null);
          connectingRef.current = false;
        }
      };

      createConnection();
    }
  }, [
    accessToken,
    refreshToken,
    userId,
    userName,
    userEmail,
    isConnecting,
    connectingApp,
    createConnectionMutation,
    createDatasetMutation,
    onConnectionChange,
    setAuthDetails,
  ]);

  const handleConfigComplete = useCallback(() => {
    setPostConnectionConfig(null);
  }, []);

  const handleConfigure = useCallback(
    (connectionId: string, connectionType: string, displayName: string) => {
      setPostConnectionConfig({
        credentialId: connectionId,
        appName: connectionType,
        displayName,
      });
    },
    [],
  );

  return {
    connectingApp,
    isConnecting,
    postConnectionConfig,
    handleConnectApp,
    connectAppByShortName,
    handleConfigComplete,
    handleConfigure,
    appsRequiringConfig: APPS_REQUIRING_CONFIG,
    appsWithAutoDataset: APPS_WITH_AUTO_DATASET,
    basicAuthApp,
    closeBasicAuthModal,
    handleBasicAuthSuccess,
    apiKeyApp,
    closeApiKeyModal,
    handleApiKeySuccess,
  };
}
